import { useMemo, useState } from 'react'
import Navbar from '@app/components/Navbar/Navbar'
import Footer from '@app/components/Footer/Footer'
import { FormattedMessage, useIntl } from 'react-intl'
import Fuse from 'fuse.js'
import { HashLink } from 'react-router-hash-link'
import { unflatten } from 'flat'
import { UnflattenedLangProps } from '@app/lang/types'
import Question from './Question'
import Search from './Search'
import S from './Faq.styles'

interface FAQItem {
  q: string
  a: string
}

interface FAQTopic {
  id: number
  title: string
  icon: string
  items: FAQItem[]
}

const FAQ = () => {
  const { messages } = useIntl()
  const [matches, setMatches] = useState<Fuse.FuseResult<FAQTopic>[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const data = useMemo(() => {
    const source = unflatten(messages) as UnflattenedLangProps

    const next = source?.faq_page?.content?.topic_list?.topics?.map(topic => ({
      id: topic.id,
      title: topic.title,
      icon: topic.icon,
      items: topic.questions.map(question => ({
        q: question.question,
        a: question.answer,
      })),
    }))

    return next ?? []
  }, [messages])

  const fuse = new Fuse(data, {
    includeScore: true,
    includeMatches: true,
    keys: ['items.q'],
  })

  const matchCount = useMemo(() => matches.reduce((acc, curr) => acc + (curr.matches?.length ?? 0), 0), [matches])

  const search = (value: string) => {
    const res = fuse.search(value)
    setMatches(res)
    setIsSearching(value.length > 0)
  }

  return (
    <>
      <Navbar />
      <main>
        <S.Banner>
          <h1>
            <FormattedMessage id="faq_page.content.search.title" />
          </h1>
          <Search
            placeholder={messages['faq_page.content.search.placeholder_text'] as string}
            onChange={search}
            onClear={() => setMatches([])}
          />
        </S.Banner>
        <S.Content>
          {!isSearching && (
            <S.QuickLinks>
              <h4>
                <FormattedMessage id="static.page.faq.quick_links_title" />
              </h4>
              <ul>
                {data.map(d => (
                  <li key={d.title}>
                    <HashLink to={`#${d.title}`}>
                      <img src={d.icon} alt="" />
                      <span>{d.title}</span>
                    </HashLink>
                  </li>
                ))}
              </ul>
            </S.QuickLinks>
          )}
          <h4>
            {isSearching ? (
              <span>
                <FormattedMessage id="static.page.faq.search_title" /> • {matchCount}{' '}
                <FormattedMessage id="static.page.faq.search_matches" />
              </span>
            ) : (
              <FormattedMessage id="faq_page.content.topic_list.title" />
            )}
          </h4>
          <S.TopicList>
            {isSearching ? (
              <>
                {matches.map(m => (
                  <S.Topic key={m.item.title}>
                    <header className="small">
                      <S.Icon>
                        <img src={m.item.icon} alt="" />
                      </S.Icon>
                      <h3>{m.item.title}</h3>
                    </header>
                    <ul>
                      {m.matches?.map(item => (
                        <Question
                          key={item.refIndex}
                          question={item.value ?? ''}
                          answer={data[m.refIndex].items[item.refIndex ?? 0].a}
                        />
                      ))}
                    </ul>
                  </S.Topic>
                ))}
              </>
            ) : (
              data.map(d => (
                <S.Topic key={d.title} id={d.title}>
                  <header>
                    <S.Icon>
                      <img src={d.icon} alt="" />
                    </S.Icon>
                    <h3>{d.title}</h3>
                  </header>
                  <ul>
                    {d.items.map(item => (
                      <Question key={item.q} question={item.q} answer={item.a} />
                    ))}
                  </ul>
                </S.Topic>
              ))
            )}
          </S.TopicList>
        </S.Content>
      </main>
      <Footer />
    </>
  )
}

export default FAQ
